import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

/** Waiting: an advanced turn still researching, before it can be queued. */
export const Status = {
  Queued: 0,
  Processing: 1,
  Waiting: 2,
  Done: 3,
  Failed: 4,
  Cancelled: 5,
} as const;
/** Not finished yet: waiting on research, queued, or being generated. */
export const isInProgress = (status: number) =>
  status === Status.Waiting || status === Status.Queued || status === Status.Processing;
export const statusLabel: Record<number, string> = {
  0: 'Queued',
  1: 'Generating',
  2: 'Researching',
  3: 'Done',
  4: 'Failed',
  5: 'Cancelled',
};

export interface Query {
  id: string;
  query: string;
  model: string;
  status: number;
  result: string | null;
  error_message: string | null;
  processing_time_ms: number | null;
  generation_ms: number | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  /** Credits this query cost. */
  cost: number | null;
  date: string;
  finished: string | null;
  turn: number;
}

/** Credits per million tokens. */
export interface Price {
  in: number;
  out: number;
}

export interface ModelInfo {
  name: string;
  nodes: number;
  /** Requests in the last week. */
  requests?: number;
  /** The current price (including any surge). */
  price: Price;
  base: Price;
}

/** Model names A–Z, with numbers in order: qwen3:4b before qwen3:14b. */
export const byModelName = (a: string, b: string) => a.localeCompare(b, 'en', { numeric: true });

/** Credits for display: whole numbers when large, more precision when small. */
export function formatCredits(credits: number): string {
  if (credits === 0) return '0';
  if (Math.abs(credits) >= 100) return Math.round(credits).toLocaleString();
  if (Math.abs(credits) >= 1)
    return credits.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return credits.toLocaleString(undefined, { maximumSignificantDigits: 2 });
}

/** The server's error message, for showing to the user. */
export function errorMessage(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    return (
      err.error?.error || (err.status === 0 ? 'Network error. Check your connection.' : err.message)
    );
  }
  return (err as Error)?.message ?? 'Something went wrong.';
}

/** Promise-based calls for actions; reads use httpResource directly. */
@Injectable({ providedIn: 'root' })
export class Api {
  private http = inject(HttpClient);

  get<T>(url: string): Promise<T> {
    return firstValueFrom(this.http.get<T>(url));
  }
  post<T>(url: string, body: unknown = {}): Promise<T> {
    return firstValueFrom(this.http.post<T>(url, body));
  }
  put<T>(url: string, body: unknown = {}): Promise<T> {
    return firstValueFrom(this.http.put<T>(url, body));
  }
  delete<T>(url: string): Promise<T> {
    return firstValueFrom(this.http.delete<T>(url));
  }
}

/** Rewards in dollars: cents once there's a cent to show, more precision below that. */
export function formatUsd(usd: number): string {
  if (usd === 0) return '$0.00';
  if (Math.abs(usd) >= 0.01)
    return usd.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  return '$' + usd.toLocaleString(undefined, { maximumSignificantDigits: 2 });
}
